import { useCallback, useMemo } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import Box from "@ui-kit/layout/Box/Box.web";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import ItemHeader from "../ItemHeader/ItemHeader.web";
import ItemBody from "../ItemBody/ItemBody.web";
import { CartLocationItemProps } from "./CartLocationItem.props";

const CartLocationItem = (props: CartLocationItemProps) => {
  const { t } = useTranslation("seating");
  const { removeLocationPermission } = useAssignedSeatingHelpers();
  const { getInfoRequiredLocationQtyInCart, seatingEventData } =
    useSeatingState();

  const { formattedData, disableItemDeletion, venueData } = props;

  // get the full item type object to display item properties
  const itemObj = useMemo(() => {
    const itemTypes =
      seatingEventData?.ticket_types ??
      seatingEventData?.tickettype_set ??
      seatingEventData?.membership_levels ??
      [];
    return itemTypes?.find(
      (item: { id: number }) =>
        item.id === formattedData.itemGroup?.type ||
        item.id === formattedData.itemGroup?.membership_level
    );
  }, [seatingEventData, formattedData]);

  const getRemoveFunction = useCallback(() => {
    // default final quantity is 0
    let finalQuantity = 0;
    const itemId =
      formattedData.itemGroup?.type ??
      formattedData.itemGroup?.membership_level;

    // info_required items are split in the cart
    // to remove one, we need to reduce the total quantity by - 1
    if (formattedData.itemGroup?.info_required && itemId) {
      finalQuantity = getInfoRequiredLocationQtyInCart(
        itemId,
        Number(formattedData.permissionId),
        1
      );
      // reduce quantity by 1
      finalQuantity -= 1;
    }
    return () =>
      removeLocationPermission(
        itemObj.id,
        Number(formattedData.permissionId),
        finalQuantity
      );
  }, [
    itemObj,
    formattedData,
    removeLocationPermission,
    getInfoRequiredLocationQtyInCart,
  ]);

  const headerVals = useMemo(
    () => [
      {
        id: 0,
        title: t("Section"),
        value: formattedData.location?.segment_identifier ?? "",
      },
      {
        id: 1,
        title: t("Area"),
        value: formattedData.location?.identifier ?? "",
      },
      {
        id: 2,
        title: t("Qty"),
        value: formattedData.location?.count ?? 0,
      },
    ],
    [t, formattedData]
  );

  return (
    <Box
      m="0px 20px"
      p="xs"
      borderRadius="md"
      backgroundColor="gray.200"
      mb="md"
      key={formattedData.permissionId}
    >
      <ItemHeader
        headerVals={headerVals}
        removeItem={getRemoveFunction()}
        disableRemoveButton={disableItemDeletion}
      />

      <Divider my="xs" />

      <ItemBody
        formattedData={formattedData}
        itemObj={itemObj}
        venueData={venueData}
        seatingEventName={seatingEventData?.name}
        isLimitedVisibility={false}
        isAccessible={false}
      />
    </Box>
  );
};

export default CartLocationItem;
